import { Box, Stack, Typography } from '@mui/material';
import {
	type AvailableReportKey,
	type CreateJobResponse,
	isClosedJobStatus,
	type JobResponse,
	type ReportCatalogEntry,
	type ReportCatalogKey,
	type ReportGroupKey,
	type ReportType,
	reportGroups,
} from '@parkchirp/reporting';
import { useEffect, useMemo, useState } from 'react';
import { ReportingLayout } from './ReportingLayout';
import { ReportStatusPanel } from './ReportStatusPanel';
import { reportComponents } from './report-components';
import { useCancelReport } from './useCancelReport';
import { useCreateReport } from './useCreateReport';
import { useReportSse } from './useReportSse';

type Props = {
	authToken: string;
	baseUrl: string;
	organizationId?: number | null;
	isSuperAdmin?: boolean;
	onError: (message: string) => void;
	onValidationError: (message: string) => void;
	canAccessReport?: (reportKey: ReportCatalogKey) => boolean;
	reportGroupKeys?: ReportGroupKey[];
	showReportGroupHeaders?: boolean;
	onGeneratingReportChange?: (isGeneratingReport: boolean) => void;
};

export type ReportingPageReportGroupKey = ReportGroupKey;

const getReportFromCatalog = (key: ReportType | null | undefined): ReportCatalogEntry | null => {
	if (!key) {
		return null;
	}

	for (const { reports } of reportGroups) {
		const report = reports.find((r: ReportCatalogEntry) => r.key === key);

		if (report) {
			return report;
		}
	}

	return null;
};

const CANCELLED_PANEL_DISMISS_MS = 2500;

const downloadFile = (url: string, fileName: string) => {
	const link = document.createElement('a');
	link.href = url;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	link.remove();
};

const jobIsRunning = (createdJob: CreateJobResponse | null, job: JobResponse | null) => {
	if (!createdJob) {
		return false;
	}

	if (job && job.jobId === createdJob.jobId) {
		return !isClosedJobStatus(job.status);
	}

	return !isClosedJobStatus(createdJob.status);
};

export const ReportingPage = ({
	authToken,
	baseUrl,
	organizationId,
	isSuperAdmin,
	onError,
	onValidationError,
	canAccessReport = () => true,
	reportGroupKeys,
	showReportGroupHeaders,
	onGeneratingReportChange,
}: Props) => {
	const [selectedReportKey, setSelectedReportKey] = useState<AvailableReportKey | null>(null);
	const [isStatusDismissed, setIsStatusDismissed] = useState(false);
	const [formSessionKey, setFormSessionKey] = useState(0);
	const [cancelRequestedJobId, setCancelRequestedJobId] = useState<string | null>(null);

	const {
		clearCreatedJob,
		createReport,
		createdJob,
		error: createError,
		isCreatingReport,
	} = useCreateReport({
		authToken,
		baseUrl,
	});

	const {
		cancelReport,
		isCancellingReport,
		error: cancelError,
	} = useCancelReport({
		authToken,
		baseUrl,
	});

	const { job, error: sseError } = useReportSse({
		authToken,
		baseUrl,
		jobId: createdJob?.jobId,
	});

	const selectedReport = useMemo(() => {
		return getReportFromCatalog(selectedReportKey);
	}, [selectedReportKey]);

	const jobReport = getReportFromCatalog(job?.reportType);

	const ReportForm = useMemo(() => {
		if (!selectedReportKey) {
			return null;
		}

		return reportComponents[selectedReportKey];
	}, [selectedReportKey]);

	const isGeneratingReport = useMemo(() => {
		return isCreatingReport || jobIsRunning(createdJob, job);
	}, [createdJob, isCreatingReport, job]);

	useEffect(() => {
		onGeneratingReportChange?.(isGeneratingReport);
	}, [isGeneratingReport, onGeneratingReportChange]);

	useEffect(() => {
		if (createdJob) {
			setIsStatusDismissed(false);
			setCancelRequestedJobId(null);
		}
	}, [createdJob]);

	useEffect(() => {
		const error = createError ?? cancelError ?? sseError;

		if (error) {
			onError(error);
		}
	}, [cancelError, createError, onError, sseError]);

	useEffect(() => {
		if (job?.status !== 'cancelled') {
			return;
		}

		const timeout = setTimeout(() => {
			clearCreatedJob();
			setIsStatusDismissed(true);
		}, CANCELLED_PANEL_DISMISS_MS);

		return () => clearTimeout(timeout);
	}, [clearCreatedJob, job?.status]);

	useEffect(() => {
		const selectedReportIsVisible = reportGroups
			.filter((group) => !reportGroupKeys || reportGroupKeys.includes(group.key))
			.some((group) => group.reports.some((report) => report.key === selectedReportKey));

		if (selectedReportKey && (!canAccessReport(selectedReportKey) || !selectedReportIsVisible)) {
			clearCreatedJob();
			setIsStatusDismissed(true);
			setSelectedReportKey(null);
		}
	}, [canAccessReport, clearCreatedJob, reportGroupKeys, selectedReportKey]);

	const handleCancel = async () => {
		if (!createdJob || cancelRequestedJobId === createdJob.jobId) {
			return;
		}

		setCancelRequestedJobId(createdJob.jobId);

		const isCancelled = await cancelReport(createdJob.jobId);

		if (!isCancelled) {
			setCancelRequestedJobId(null);
		}
	};

	const handleDownload = (job: JobResponse) => {
		if (!createdJob || job.jobId !== createdJob.jobId || job.status !== 'completed' || !job.output) {
			return;
		}

		downloadFile(job.output.downloadUrl, job.output.fileName);
	};

	const showStatusPanel = useMemo(() => {
		if (!job || !createdJob) {
			return false;
		}

		if (!jobReport) {
			return false;
		}

		if (isStatusDismissed) {
			return false;
		}

		if (job.jobId !== createdJob.jobId) {
			return false;
		}

		return true;
	}, [job, createdJob, jobReport, isStatusDismissed]);

	const handleSelectReport = (reportKey: AvailableReportKey) => {
		clearCreatedJob();
		setIsStatusDismissed(true);
		setSelectedReportKey(reportKey);
		setFormSessionKey((current) => current + 1);
	};

	return (
		<ReportingLayout
			selectedReportKey={selectedReportKey}
			navigationDisabled={isGeneratingReport}
			canAccessReport={canAccessReport}
			onSelectReport={handleSelectReport}
			reportGroupKeys={reportGroupKeys}
			showReportGroupHeaders={showReportGroupHeaders}
		>
			{selectedReport && ReportForm ? (
				<Stack spacing={2.5}>
					<Box
						sx={{
							borderBottom: 1,
							borderColor: 'divider',
							pb: 1.5,
						}}
					>
						<Typography variant='h5' fontWeight={700}>
							{selectedReport.title}
						</Typography>
					</Box>

					<ReportForm
						key={`${selectedReportKey}-${formSessionKey}`}
						createReport={createReport}
						isGeneratingReport={isGeneratingReport}
						organizationId={organizationId}
						isSuperAdmin={isSuperAdmin}
						onReset={() => {
							clearCreatedJob();
							setIsStatusDismissed(true);
							setFormSessionKey((current) => current + 1);
						}}
						onValidationError={onValidationError}
					/>
				</Stack>
			) : (
				<Box
					sx={{
						alignItems: 'center',
						display: 'flex',
						justifyContent: 'center',
						minHeight: 320,
					}}
				>
					<Typography color='text.secondary'>Select a report to configure.</Typography>
				</Box>
			)}

			{showStatusPanel && (
				<ReportStatusPanel
					job={job}
					reportTitle={jobReport.title}
					isCancellingReport={isCancellingReport || cancelRequestedJobId === job.jobId}
					onCancel={handleCancel}
					onDismiss={() => setIsStatusDismissed(true)}
					onDownload={handleDownload}
				/>
			)}
		</ReportingLayout>
	);
};
